var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { createAsyncThunk, createSlice, isAnyOf } from '@reduxjs/toolkit';
import UPDATE_PRODUCT_VARIANT from 'api/mutations/UPDATE_PRODUCT_VARIANT';
import UPDATE_PRODUCT_VARIANT_SNAPSHOT_COST from 'api/mutations/UPDATE_PRODUCT_VARIANT_SNAPSHOT_COST';
import GET_PRODUCTS_COSTS from 'api/queries/GET_PRODUCTS_COSTS';
import client from '../api/environment';
import { addAndAssignSpecificationComponent, addAndAssignSpecificationComponents, removeSpecificationComponent, setVariantForAllColor, setVariantForAllSizes, toggleVariedOnColorSpecificationComponent, toggleVariedOnSizeSpecificationComponent, updateColorToVariantAssignment, updateSizeToVariantAssignment, } from './specificationComponentsSlice';
const initialState = {
    items: [],
    variantCostDetails: null,
    status: {
        fetch: 'idle',
        update: 'idle',
        updateProductVariant: 'idle',
    },
    errors: {
        fetch: null,
        update: null,
        updateProductVariant: null,
    },
    refetchFlags: {
        fetch: false,
        update: false,
        updateProductVariant: false,
    },
};
export const fetchSpecificationComponentsCosts = createAsyncThunk('specificationComponentsCosts/fetchSpecificationComponentsCosts', (_a, _b) => __awaiter(void 0, [_a, _b], void 0, function* ({ specificationId }, { rejectWithValue }) {
    try {
        const componentsCostsData = yield client.query({
            query: GET_PRODUCTS_COSTS,
            variables: { modelSpecificationId: specificationId },
            fetchPolicy: 'network-only',
        });
        return componentsCostsData.data.node.components.edges.map((item) => item.node);
    }
    catch (error) {
        const apolloError = error;
        return rejectWithValue(apolloError.message);
    }
}));
export const updateComponentVariantPrice = createAsyncThunk('specificationComponentsCosts/updateComponentVariantPrice', (_a, _b) => __awaiter(void 0, [_a, _b], void 0, function* ({ price, currencyId, productVariantSnapshotId, }, { rejectWithValue }) {
    try {
        const responseData = yield client.mutate({
            mutation: UPDATE_PRODUCT_VARIANT_SNAPSHOT_COST,
            variables: { price, currencyId, productVariantSnapshotId },
        });
        return responseData.data.updateProductVariantSnapshotCost.productVariantSnapshot;
    }
    catch (error) {
        const apolloError = error;
        return rejectWithValue(apolloError.message);
    }
}));
export const updateProductVariant = createAsyncThunk('specificationComponentsCosts/updateProductVariant', (_a, _b) => __awaiter(void 0, [_a, _b], void 0, function* ({ productVariantId, price, currencyId, }, { rejectWithValue }) {
    try {
        const responseData = yield client.mutate({
            mutation: UPDATE_PRODUCT_VARIANT,
            variables: { productVariantId, price, currencyId },
        });
        return responseData.data;
    }
    catch (error) {
        const apolloError = error;
        return rejectWithValue(apolloError.message);
    }
}));
const specificationComponentsCostsSlice = createSlice({
    name: 'specificationComponentsCosts',
    initialState,
    reducers: {
        resetSpecificationComponentsCosts(state) {
            state.items = [];
            state.variantCostDetails = null;
            state.status = {
                fetch: 'idle',
                update: 'idle',
                updateProductVariant: 'idle',
            };
            state.errors = {
                fetch: null,
                update: null,
                updateProductVariant: null,
            };
            state.refetchFlags = {
                fetch: false,
                update: false,
                updateProductVariant: false,
            };
        },
        updateStoreComponentVariantPrice: (state, action) => {
            const { price, currency, currencyId, productVariantSnapshotId } = action.payload;
            state.items.forEach((component) => {
                const snapshot = component.assignedProductVariantSnapshots.edges.find((edge) => edge.node.id === productVariantSnapshotId);
                if (snapshot && price !== undefined) {
                    snapshot.node.price = price;
                }
                if (snapshot && currency && currencyId) {
                    snapshot.node.currency = {
                        isoCode: currency,
                        id: currencyId,
                    };
                }
            });
        },
        updateStoreProductPrice: (state, action) => {
            const { price, currencyId, productVariantSnapshotId } = action.payload;
            state.items.forEach((component) => {
                const snapshot = component.assignedProductVariantSnapshots.edges.find((edge) => edge.node.id === productVariantSnapshotId);
                if (snapshot && price) {
                    snapshot.node.productVariant.currentPrice.price = price;
                }
                if (snapshot && currencyId) {
                    snapshot.node.productVariant.currentPrice.currency.id = currencyId;
                }
            });
        },
    },
    extraReducers: (builder) => {
        builder
            // fetchSpecificationComponentsCosts
            .addCase(fetchSpecificationComponentsCosts.pending, (state) => {
            state.status.fetch = 'loading';
        })
            .addCase(fetchSpecificationComponentsCosts.fulfilled, (state, action) => {
            state.items = action.payload;
            state.status.fetch = 'succeeded';
        })
            .addCase(fetchSpecificationComponentsCosts.rejected, (state, action) => {
            state.status.fetch = 'failed';
            state.errors.fetch =
                action.payload || 'Failed to fetch specification production quantities';
        })
            .addCase(updateComponentVariantPrice.pending, (state) => {
            state.status.update = 'loading';
        })
            .addCase(updateComponentVariantPrice.fulfilled, (state) => {
            state.status.update = 'succeeded';
        })
            .addCase(updateComponentVariantPrice.rejected, (state, action) => {
            state.status.update = 'failed';
            state.errors.update =
                action.payload || 'Failed to fetch specification production quantities';
        })
            .addCase(updateProductVariant.pending, (state) => {
            state.status.updateProductVariant = 'loading';
        })
            .addCase(updateProductVariant.fulfilled, (state) => {
            state.status.updateProductVariant = 'succeeded';
        })
            .addCase(updateProductVariant.rejected, (state, action) => {
            state.status.updateProductVariant = 'failed';
            state.errors.updateProductVariant =
                action.payload || 'Failed to fetch specification production quantities';
        })
            // Assigned variants depend on components and their color/size assignments.
            .addMatcher(isAnyOf(addAndAssignSpecificationComponent.fulfilled, addAndAssignSpecificationComponents.fulfilled, removeSpecificationComponent.fulfilled, toggleVariedOnColorSpecificationComponent.fulfilled, setVariantForAllColor.fulfilled, updateColorToVariantAssignment.fulfilled, toggleVariedOnSizeSpecificationComponent.fulfilled, setVariantForAllSizes.fulfilled, updateSizeToVariantAssignment.fulfilled), (state) => {
            state.status.fetch = 'idle';
        });
    },
});
export const { resetSpecificationComponentsCosts, updateStoreComponentVariantPrice, updateStoreProductPrice, } = specificationComponentsCostsSlice.actions;
export const selectComponentsCostsStatus = (state) => state.specificationComponentsCosts.status;
export const selectComponentsCostsErrors = (state) => state.specificationComponentsCosts.errros;
export const selectComponentsCostsRefetchFlags = (state) => state.specificationComponentsCosts.refetchFlags;
export const selectComponentsCostsItems = (state) => state.specificationComponentsCosts.items;
export default specificationComponentsCostsSlice.reducer;
